import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import landingService from "../landing.service";
import { Eventbus } from "../../../utils";
import { events, statusCodes } from "../../../utils/constants";
import logger from "../../../utils/logger";

const LandingServiceContext = createContext();

export function LandingServiceProvider({ children }) {
  const { darkMode } = useTheme();
  const [emails, setEmails] = useState([]);
  const [issues, setIssues] = useState([]);
  const [grandTotals, setGrandTotals] = useState({});
  const [isEmailsLoading, setIsEmailsLoading] = useState(true);
  const [isIssuesLoading, setIsIssuesLoading] = useState(true);
  const [isGrandTotalsLoading, setIsGrandTotalsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});

  // Get CSS custom properties for chart colors
  const colors = useMemo(() => [
    getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim(),
    getComputedStyle(document.documentElement).getPropertyValue("--purple-color").trim(),
    getComputedStyle(document.documentElement).getPropertyValue("--secondary-color").trim(),
    getComputedStyle(document.documentElement).getPropertyValue("--warning-color").trim(),
    getComputedStyle(document.documentElement).getPropertyValue("--error-color").trim(),
  ], []);

  // Chart configuration for current issues
  const issuesChartOptions = useMemo(
    () => ({
      theme: darkMode ? "dark2" : "light2",
      colorSet: "customColorSet",
      animationEnabled: true,
      animationDuration: 500,
      height: 300,
      backgroundColor: "transparent",
      responsive: true,
      maintainAspectRatio: false,
      isLoading: isIssuesLoading,
      legend: {
        horizontalAlign: "center",
        verticalAlign: "top",
        fontSize: 20,
        fontFamily: "Arial",
      },
      title: {
        fontSize: 70,
        horizontalAlign: "center",
        text: issues?.totalIssues,
        verticalAlign: "center",
      },
      data: [
        {
          explodeOnClick: false,
          innerRadius: "90%",
          radius: "100%",
          startAngle: 270,
          type: "doughnut",
          dataPoints:
            issues && issues.statusCounts
              ? Object.entries(issues.statusCounts).map(([statusCode, count]) => ({
                  label: statusCodes[statusCode] || statusCode,
                  y: count,
                  toolTipContent: `${statusCodes[statusCode] || statusCode}: ${count}`,
                  bevelEnabled: true,
                }))
              : [],
        },
      ],
    }),
    [darkMode, issues, isIssuesLoading]
  );

  // Chart configuration for email record status
  const passedPercentChartOptions = useMemo(
    () => ({
      theme: darkMode ? "dark2" : "light2",
      colorSet: "customColorSet",
      animationEnabled: true,
      animationDuration: 500,
      height: 300,
      backgroundColor: "transparent",
      responsive: true,
      maintainAspectRatio: false,
      isLoading: isEmailsLoading,
      legend: {
        horizontalAlign: "center",
        verticalAlign: "top",
        fontSize: 20,
        fontFamily: "Arial",
      },
      title: {
        fontSize: 70,
        horizontalAlign: "center",
        text: emails?.totalEmails,
        verticalAlign: "center",
      },
      data: [
        {
          explodeOnClick: false,
          innerRadius: "90%",
          radius: "100%",
          startAngle: 270,
          type: "doughnut",
          dataPoints:
            emails && emails.statusCounts
              ? Object.entries(emails.statusCounts).map(([statusCode, count]) => ({
                  label: statusCodes[statusCode] || statusCode,
                  y: count,
                  toolTipContent: `${statusCodes[statusCode] || statusCode}: ${count}`,
                  bevelEnabled: true,
                }))
              : [],
        },
      ],
    }),
    [darkMode, emails, isEmailsLoading]
  );

  // Load data function
  const loadData = async () => {
    setIsEmailsLoading(true);
    setIsIssuesLoading(true);
    setError(null);
    try {
      await landingService.loadData();
      // Data will be updated via event bus
    } catch (err) {
      setError(err.message || "Failed to load data");
      setIsEmailsLoading(false);
      setIsIssuesLoading(false);
      logger.error("Error loading landing data:", err);
    }
  };

  // Refresh data function
  const refreshData = () => {
    loadData();
  };

  // Update stats function
  const updateStats = (newStats) => {
    setStats((prev) => ({ ...prev, ...newStats }));
  };

  function setEmailsViaEvent(emails) {
    setEmails(emails);
    setIsEmailsLoading(false);
  }

  function setIssuesViaEvent(issues) {
    setIssues(issues);
    setIsIssuesLoading(false);
  }

  function setGrandTotalsViaEvent(grandTotals) {
    setGrandTotals(grandTotals);
    setIsGrandTotalsLoading(false);
  }

  useEffect(() => {
    // Set up event listeners
    Eventbus.on(events.STATS.EMAIL_LOADED, setEmailsViaEvent);
    Eventbus.on(events.STATS.ISSUES_LOADED, setIssuesViaEvent);
    Eventbus.on(events.STATS.GRAND_TOTALS_LOADED, setGrandTotalsViaEvent);


    // Load initial data
    loadData();

    return () => {
      // Clean up event listeners
      Eventbus.off(setEmailsViaEvent);
      Eventbus.off(setIssuesViaEvent);
      Eventbus.off(setGrandTotalsViaEvent);
    };
  }, []);

  const contextValue = {
    // State
    emails,
    issues,
    grandTotals,
    isEmailsLoading,
    isIssuesLoading,
    isGrandTotalsLoading,
    error,
    stats,

    // Chart configurations
    issuesChartOptions,
    passedPercentChartOptions,

    // Colors
    colors,

    // Actions
    loadData,
    refreshData,
    updateStats,
    setEmails,
    setError,
  };

  return <LandingServiceContext.Provider value={contextValue}>{children}</LandingServiceContext.Provider>;
}

export const useLandingService = () => {
  const context = useContext(LandingServiceContext);
  if (!context) {
    throw new Error("useLandingService must be used within a LandingServiceProvider");
  }
  return context;
};

export default LandingServiceContext;
